import { createContext, type ReactNode, useContext, useRef } from 'react';

type SessionStore = { get: (key: string) => string };

const AddressSearchSessionContext = createContext<SessionStore | null>(null);

/**
 * Holds one provider search session per address group. Mapbox bills a *session*,
 * not a request, and ends one 180s after the last /suggest (we never call
 * /retrieve) — so a token is never rotated here: rotating cannot close a session
 * early, it only opens a second billable one.
 */
export const AddressSearchSessionProvider = ({ children }: { children: ReactNode }) => {
  const tokens = useRef(new Map<string, string>()).current;
  const store = useRef<SessionStore>({
    get: key => {
      const existing = tokens.get(key);
      if (existing) {
        return existing;
      }
      const minted = crypto.randomUUID();
      tokens.set(key, minted);
      return minted;
    },
  }).current;

  return (
    <AddressSearchSessionContext.Provider value={store}>
      {children}
    </AddressSearchSessionContext.Provider>
  );
};

/**
 * The session id every field of one address group shares. Falls back to a
 * per-instance token outside a provider, so a lone field still works.
 */
export const useAddressSearchSessionId = (groupKey: string | undefined): string => {
  const store = useContext(AddressSearchSessionContext);
  const fallback = useRef(crypto.randomUUID()).current;
  return store && groupKey ? store.get(groupKey) : fallback;
};
